import React, {
  FC,
  ReactNode,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
} from 'react';
import classnames from 'classnames';

export interface RSFormMessageContainerProps {
  children?: ReactNode;
  descriptions?: string[];
  errors?: string[];
  isBlock?: boolean;
}

export const RSFormMessageContainer: FC<RSFormMessageContainerProps> = ({
  children,
  descriptions,
  errors,
  isBlock,
}) => {
  const childrenWrapperRef = useRef<HTMLDivElement>(null);

  const [messageWrapperWidth, setMessageWrapperWidth] = useState<
    number | undefined
  >(undefined);

  const hasMessage = useMemo(() => {
    return (
      (descriptions && descriptions.length > 0) || (errors && errors.length > 0)
    );
  }, [descriptions, errors]);

  useLayoutEffect(() => {
    if (!hasMessage) return;
    if (isBlock) return;

    const updateMessageWrapperWidth = () => {
      if (!childrenWrapperRef.current) return;

      const width = childrenWrapperRef.current.getBoundingClientRect().width;
      setMessageWrapperWidth(width);
    };

    updateMessageWrapperWidth();

    window.addEventListener('resize', updateMessageWrapperWidth);

    return () => {
      window.removeEventListener('resize', updateMessageWrapperWidth);
      setMessageWrapperWidth(undefined);
    };
  }, [children, isBlock, hasMessage]);

  return (
    <div className={classnames(!isBlock ? 'inline-block' : 'block w-full')}>
      <div
        ref={childrenWrapperRef}
        className={classnames(!isBlock ? 'inline-block' : 'block w-full')}
      >
        {children}
      </div>
      {hasMessage && (
        <div
          style={{ width: messageWrapperWidth }}
          className="mt-0.5 space-y-[2px]"
        >
          {errors && errors.length > 0 && (
            <div>
              {errors.map((error, index) => {
                return (
                  <div
                    key={`error_${error}_${index}`}
                    className="rx-text text-13 pc:text-13 font-normal text-red-700"
                  >
                    ▲ {error}
                  </div>
                );
              })}
            </div>
          )}
          {descriptions && descriptions.length > 0 && (
            <div>
              {descriptions.map((description, index) => {
                return (
                  <div
                    key={`description_${description}_${index}`}
                    className="rx-text text-13 text-gray-1000 pc:text-13 font-normal"
                  >
                    {description}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
